import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const selectCls = "h-10 w-full rounded-sm border border-zinc-300 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600";
const CATEGORIES = [
  { value: "retail", label: "Retail" },
  { value: "at_cost", label: "At Cost (COGS)" },
];
const emptyForm = { name: "", phone: "", category: "retail" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, phone: c.phone || "", category: c.category });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Customer deleted");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Customers</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Customer master with pricing category — "At Cost" customers are always billed at COGS.
          </p>
        </div>
        <Button data-testid="btn-add-customer" onClick={openCreate} className="rounded-sm bg-blue-600 text-white transition-transform hover:-translate-y-[1px] hover:bg-blue-700">
          <Plus className="mr-2 h-4 w-4" /> Add Customer
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-zinc-200 bg-white">
        <table className="w-full text-sm" data-testid="customer-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Category</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-zinc-400" data-testid="customer-empty">
                  No customers yet
                </td>
              </tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`customer-row-${c.name}`}>
                <td className="px-4 py-2.5 font-medium text-zinc-900">{c.name}</td>
                <td className="px-4 py-2.5 text-zinc-500">{c.phone || "-"}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${c.category === "at_cost" ? "border-amber-200 bg-amber-50 text-amber-700" : "border-zinc-200 bg-zinc-50 text-zinc-600"}`} data-testid={`customer-category-${c.name}`}>
                    {c.category === "at_cost" ? "At Cost (COGS)" : "Retail"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-zinc-500">{formatDate(c.created_at)}</td>
                <td className="px-4 py-2.5 text-right">
                  <button data-testid={`btn-edit-customer-${c.name}`} onClick={() => openEdit(c)} className="mr-1 rounded-sm p-1.5 text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-blue-600">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button data-testid={`btn-delete-customer-${c.name}`} onClick={() => remove(c)} className="rounded-sm p-1.5 text-zinc-500 transition-colors hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "Add Customer"}</DialogTitle>
            <DialogDescription className="sr-only">Customer details form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input data-testid="customer-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Budi Santoso" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input data-testid="customer-form-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xx" className="rounded-sm border-zinc-300" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <select data-testid="customer-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={selectCls}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {form.category === "at_cost" && (
                <p className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="customer-form-cogs-hint">
                  Invoices for this customer auto-fill item prices with the FIFO cost (COGS).
                </p>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="customer-form-submit" type="submit" disabled={saving} className="rounded-sm bg-blue-600 text-white hover:bg-blue-700">
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
